import React, { useRef, useState } from "react";
import { LogIn, ShieldCheck, Eye, EyeOff, Mail } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

// 2026-02-19 (Dave) — read the `invite` query param out of the hash
// route so we can prefill the email + surface a hint. Kept outside the
// component so the initial useState() lazy initializer can call it
// without stale-closure risk.
function readInviteEmail() {
  const h = typeof window !== "undefined" ? window.location.hash || "" : "";
  if (!h.includes("?")) return "";
  const qs = h.slice(h.indexOf("?") + 1);
  const params = new URLSearchParams(qs);
  return (params.get("invite") || "").trim().toLowerCase();
}

export default function LoginScreen() {
  const { login, error } = useAuth();
  const inviteEmail = readInviteEmail();
  const [email, setEmail] = useState(inviteEmail);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const pwRef = useRef(null);
  // 2026-02-15 (Dave) — Persist the "Keep me logged in" preference across
  // logins. Without this the checkbox always renders unchecked and users
  // who set it once forget to set it on subsequent logins, which was one
  // side of the "Keep me logged in doesn't keep me logged in" complaint.
  // Read once on mount; write on every checkbox change.
  const [rememberMe, setRememberMe] = useState(() => {
    try { return window.localStorage.getItem("ureserve.rememberMe") === "1"; }
    catch { return false; }
  });
  const setAndPersistRemember = (checked) => {
    setRememberMe(checked);
    try {
      if (checked) window.localStorage.setItem("ureserve.rememberMe", "1");
      else window.localStorage.removeItem("ureserve.rememberMe");
    } catch { /* ignore */ }
  };
  const [busy, setBusy] = useState(false);
  const [localErr, setLocalErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLocalErr("");
    if (!email.trim() || !password) {
      setLocalErr("Enter your email and password.");
      return;
    }
    setBusy(true);
    const ok = await login(email.trim().toLowerCase(), password, rememberMe);
    setBusy(false);
    // On success the parent will unmount this screen automatically.
    if (!ok) {
      // login() sets context error — mirror it locally too
      setPassword("");
    }
  };

  const shownError = localErr || error;

  return (
    <div
      className="min-h-screen bg-canvas flex items-center justify-center px-4"
      data-testid="screen-login"
    >
      <div className="w-full max-w-sm">
        {/* Brand block — full uReserve logo flush-left, badge flush-right. */}
        <div className="flex items-center justify-between mb-6" data-testid="brand-block">
          <img
            src="/ureserve-logo.png"
            alt="uReserve Planning — Reserve Fund Planning Reimagined"
            className="h-9 w-auto"
            data-testid="login-brand-logo"
          />
          <div className="w-8 h-8 rounded-[3px] bg-navy text-white flex items-center justify-center shrink-0">
            <ShieldCheck size={18} />
          </div>
        </div>

        <form
          onSubmit={submit}
          className="border border-rule bg-white p-6 flex flex-col gap-4 rounded-[8px]"
        >
          <div className="text-[11px] tracking-[0.14em] uppercase font-bold text-navy pb-1"
               style={{ borderBottom: "1px solid #EA6A47" }}>
            Sign in
          </div>

          {inviteEmail && (
            <div
              className="flex items-start gap-2 border border-navy bg-navy-wash/40 px-3 py-2 rounded-[3px] text-[11.5px] text-navy leading-snug"
              data-testid="login-invite-banner"
            >
              <Mail size={14} className="mt-0.5 shrink-0 text-navy" />
              <div>
                Welcome to uReserve. Enter the <span className="font-semibold">temporary password</span> from your invitation email below. You&apos;ll be prompted to set a permanent one after you sign in.
              </div>
            </div>
          )}

          <label className="flex flex-col gap-1">
            <span className="text-[10.5px] tracking-[0.1em] uppercase font-semibold text-slate">
              Email
            </span>
            <input
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="border border-rule rounded-[3px] px-2.5 py-2 text-[13px] focus:outline-none focus:border-navy"
              data-testid="login-email"
              autoFocus={!inviteEmail}
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-[10.5px] tracking-[0.1em] uppercase font-semibold text-slate">
              Password
            </span>
            <div className="relative">
              <input
                ref={pwRef}
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border border-rule rounded-[3px] px-2.5 py-2 pr-10 text-[13px] focus:outline-none focus:border-navy"
                data-testid="login-password"
                autoFocus={!!inviteEmail}
              />
              <button
                type="button"
                onClick={() => {
                  // 2026-02-19 (Dave) — after toggling visibility, park
                  // the cursor at the START of the field so the user
                  // can see the beginning of what they typed. Default
                  // browser behaviour on some browsers is to jump to
                  // end of input after a type-attribute swap.
                  setShowPassword((v) => !v);
                  requestAnimationFrame(() => {
                    const el = pwRef.current;
                    if (!el) return;
                    try {
                      el.focus();
                      el.setSelectionRange(0, 0);
                      el.scrollLeft = 0;
                    } catch { /* selectionRange not supported on some input types */ }
                  });
                }}
                aria-label={showPassword ? "Hide password" : "Show password"}
                title={showPassword ? "Hide password" : "Show password"}
                tabIndex={-1}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1.5 text-slate hover:text-navy transition-colors"
                data-testid="login-password-toggle"
              >
                {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </label>

          {shownError && (
            <div
              className="text-[11.5px] text-coral bg-coral/5 border border-coral/30 rounded-[3px] px-2 py-1.5"
              data-testid="login-error"
            >
              {shownError}
            </div>
          )}

          <label
            className="flex items-center gap-2 text-[12px] text-slate cursor-pointer select-none mt-1"
            data-testid="login-remember-label"
          >
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setAndPersistRemember(e.target.checked)}
              className="w-3.5 h-3.5 accent-navy"
              data-testid="login-remember-checkbox"
            />
            Keep me logged in on this device
          </label>

          <button
            type="submit"
            disabled={busy}
            className="mt-1 w-full bg-navy text-white py-2 text-[13px] font-semibold rounded-[3px] flex items-center justify-center gap-2 hover:bg-navy/90 disabled:opacity-50"
            data-testid="login-submit"
          >
            <LogIn size={14} />
            {busy ? "Signing in…" : "Sign in"}
          </button>

          {/* 2026-02-19 (Dave) — self-service Forgot password? link. Opens
              an inline mini-form that POSTs /api/auth/forgot-password, then
              swaps to a "check your inbox" confirmation. */}
          <div className="text-center -mt-1">
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent("ureserve:open-forgot-password", { detail: { email: email.trim().toLowerCase() } }))}
              className="text-[11px] text-navy hover:text-coral underline underline-offset-2 transition-colors"
              data-testid="login-forgot-password-link"
            >
              Forgot password?
            </button>
          </div>
        </form>

        <div className="text-[10.5px] text-slate mt-4 text-center leading-relaxed">
          Contact your administrator if you can&apos;t sign in.
        </div>
      </div>
    </div>
  );
}
